/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState } from "react";

import { BONUS_BANDS } from "../../lib/data/bonus";
export function BonusCalculatorPage({ user, lang }) {
  const [sales,      setSales]      = useState("");
  const [foodCost,   setFoodCost]   = useState("");
  const [rating,     setRating]     = useState("");
  const [auditPass,  setAuditPass]  = useState(null);  // true/false/null
  const [quizPass,   setQuizPass]   = useState(null);
  const [otjPass,    setOtjPass]    = useState(null);

  const TARGET_SALES    = 80000;
  const TARGET_FC       = 0.35;
  const TARGET_RATING   = 9.0;
  const FC_GATE         = 0.38;
  const RATING_GATE     = 7.8;

  // ── Gate checks ──────────────────────────────────────────────────────────────
  const auditFailed  = auditPass === false;
  const fcGateFailed = foodCost !== "" && parseFloat(foodCost)/100 > FC_GATE;
  const ratingFailed = rating   !== "" && parseFloat(rating) < RATING_GATE;
  const opGateFailed = auditFailed || fcGateFailed || ratingFailed;

  // ── Weighted score ────────────────────────────────────────────────────────────
  function calcScore() {
    if (sales === "" || foodCost === "" || rating === "" || auditPass === null) return null;

    const salesAch  = Math.min(parseFloat(sales) / TARGET_SALES, 1.5);
    const fcActual  = parseFloat(foodCost) / 100;
    const fcAch     = fcActual <= TARGET_FC ? 1 + (TARGET_FC - fcActual) / TARGET_FC : Math.max(0, 1 - (fcActual - TARGET_FC) / TARGET_FC);
    const ratAch    = Math.min(parseFloat(rating) / TARGET_RATING, 1.5);
    const auditAch  = auditPass ? 1 : 0;

    const score = (salesAch*0.25 + fcAch*0.35 + ratAch*0.20 + auditAch*0.20) * 100;
    return Math.round(score);
  }

  const score = calcScore();
  const band  = score !== null ? BONUS_BANDS.find(b => score >= b.min && score <= b.max) : null;
  const bonus = (opGateFailed || (quizPass === false || otjPass === false)) ? 0 : (band?.bonus ?? 0);

  function ScoreBar({ value, max=100, color }) {
    const pct = Math.min((value/max)*100, 100);
    return (
      <div style={{ height:8, background:"var(--surface3)", borderRadius:4, overflow:"hidden", marginTop:6 }}>
        <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:4, transition:"width 0.4s" }}/>
      </div>
    );
  }

  return (
    <div className="anim-fade">
      <div style={{ marginBottom:20 }}>
        <div style={{ fontWeight:800, fontSize:18, color:"var(--text-primary)" }}>
          💰 {lang==="gr" ? "Υπολογιστής Bonus" : "Bonus Calculator"}
        </div>
        <div style={{ fontSize:13, color:"var(--text-muted)", marginTop:4 }}>
          {lang==="gr"
            ? "Target bonus €150 στο 100%. Εισάγετε τα πραγματικά νούμερα για να δείτε το αποτέλεσμα."
            : "Target bonus €150 at 100% performance. Enter actual numbers to see the result."}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        {/* Sales */}
        <div style={{ background:"var(--surface)", border:"1.5px solid var(--border)", borderRadius:16, padding:16 }}>
          <div style={{ fontSize:10, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
            📈 {lang==="gr"?"Πωλήσεις (€)":"Sales (€)"} <span style={{ color:"var(--green)" }}>25%</span>
          </div>
          <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:6 }}>Target: €{TARGET_SALES.toLocaleString()}</div>
          <input type="number" className="form-input" placeholder="e.g. 82000"
            value={sales} onChange={e=>setSales(e.target.value)} style={{ marginBottom:6 }}/>
          {sales !== "" && <ScoreBar value={parseFloat(sales)/TARGET_SALES*100} max={110} color="#22C55E"/>}
        </div>

        {/* Food Cost */}
        <div style={{ background:"var(--surface)", border:`1.5px solid ${fcGateFailed?"#FCA5A5":"var(--border)"}`, borderRadius:16, padding:16 }}>
          <div style={{ fontSize:10, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
            🍽️ {lang==="gr"?"Food Cost (%)":"Food Cost (%)"} <span style={{ color:"var(--green)" }}>35%</span>
          </div>
          <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:6 }}>Target: ≤{TARGET_FC*100}% · Gate: &lt;{FC_GATE*100}%</div>
          <input type="number" className="form-input" placeholder="e.g. 33"
            value={foodCost} onChange={e=>setFoodCost(e.target.value)} style={{ marginBottom:6, borderColor: fcGateFailed?"#EF4444":"" }}/>
          {fcGateFailed && <div style={{ fontSize:11, color:"#DC2626", fontWeight:700 }}>⛔ Gate failed — bonus = €0</div>}
          {foodCost !== "" && !fcGateFailed && <ScoreBar value={Math.max(0,(FC_GATE-(parseFloat(foodCost)/100))/FC_GATE*100+50)} max={100} color="#22C55E"/>}
        </div>

        {/* Customer Rating */}
        <div style={{ background:"var(--surface)", border:`1.5px solid ${ratingFailed?"#FCA5A5":"var(--border)"}`, borderRadius:16, padding:16 }}>
          <div style={{ fontSize:10, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
            ⭐ {lang==="gr"?"Αξιολόγηση Wolt":"Wolt Rating"} <span style={{ color:"var(--green)" }}>20%</span>
          </div>
          <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:6 }}>Target: {TARGET_RATING} · Gate: ≥{RATING_GATE}</div>
          <input type="number" step="0.1" className="form-input" placeholder="e.g. 9.2"
            value={rating} onChange={e=>setRating(e.target.value)} style={{ marginBottom:6, borderColor: ratingFailed?"#EF4444":"" }}/>
          {ratingFailed && <div style={{ fontSize:11, color:"#DC2626", fontWeight:700 }}>⛔ Gate failed — bonus = €0</div>}
          {rating !== "" && !ratingFailed && <ScoreBar value={parseFloat(rating)/TARGET_RATING*100} max={110} color="#22C55E"/>}
        </div>

        {/* Audit */}
        <div style={{ background:"var(--surface)", border:`1.5px solid ${auditFailed?"#FCA5A5":"var(--border)"}`, borderRadius:16, padding:16 }}>
          <div style={{ fontSize:10, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
            🔍 {lang==="gr"?"Αποτέλεσμα Audit":"Audit Result"} <span style={{ color:"var(--green)" }}>20%</span>
          </div>
          <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:10 }}>
            {lang==="gr"?"Fail → Bonus = €0":"Fail = instant €0 bonus"}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {[["pass","✅ PASS",false],["fail","❌ FAIL",true]].map(([val,label,isFail]) => (
              <button key={val} onClick={() => setAuditPass(!isFail)}
                style={{ flex:1, padding:"8px 0", border:"1.5px solid", borderRadius:10, cursor:"pointer", fontWeight:800, fontSize:12, fontFamily:"DM Sans,sans-serif", transition:"all 0.15s",
                  borderColor: auditPass===(!isFail) ? (isFail?"#EF4444":"var(--green)") : "var(--border)",
                  background:  auditPass===(!isFail) ? (isFail?"#FEE2E2":"var(--green-light)") : "var(--surface2)",
                  color:       auditPass===(!isFail) ? (isFail?"#DC2626":"var(--green)") : "var(--text-muted)",
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Operational Gate: Quiz + OTJ */}
      <div style={{ background:"var(--surface)", border:"1.5px solid var(--border)", borderRadius:16, padding:16, marginBottom:14 }}>
        <div style={{ fontSize:12, fontWeight:800, color:"var(--text-primary)", marginBottom:12 }}>
          🎓 {lang==="gr"?"Αξιολόγηση Εκπαίδευσης (απαιτείται Pass και στα δύο)":"Training Assessment (must pass both)"}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {[
            { label: lang==="gr"?"Quiz (≥90% = Pass, 80-90% = Conditional)":"Quiz (≥90% Pass, 80–90% Conditional)", state: quizPass, set: setQuizPass },
            { label: lang==="gr"?"On-the-Job (≥85% = Pass, 70-85% = Conditional)":"On-the-Job (≥85% Pass, 70–85% Conditional)", state: otjPass, set: setOtjPass },
          ].map(({ label, state, set }) => (
            <div key={label}>
              <div style={{ fontSize:11, color:"var(--text-muted)", fontWeight:600, marginBottom:8 }}>{label}</div>
              <div style={{ display:"flex", gap:6 }}>
                {[["✅ Pass",true],["❌ Fail",false]].map(([l,v]) => (
                  <button key={l} onClick={() => set(v)}
                    style={{ flex:1, padding:"7px 0", border:"1.5px solid", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, fontFamily:"DM Sans,sans-serif",
                      borderColor: state===v ? (v?"var(--green)":"#EF4444") : "var(--border)",
                      background:  state===v ? (v?"var(--green-light)":"#FEE2E2") : "var(--surface2)",
                      color:       state===v ? (v?"var(--green)":"#DC2626") : "var(--text-muted)",
                    }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Result */}
      {score !== null && (
        <div style={{ background: bonus > 0 ? "var(--green)" : "#FEF0ED", border:`2px solid ${bonus>0?"var(--green)":"#FACFC8"}`, borderRadius:20, padding:"24px 28px", textAlign:"center" }}>
          {opGateFailed || quizPass===false || otjPass===false ? (
            <>
              <div style={{ fontSize:32, marginBottom:8 }}>⛔</div>
              <div style={{ fontWeight:900, fontSize:22, color:"#DC2626", marginBottom:6 }}>
                {lang==="gr"?"Bonus: €0":"Bonus: €0"}
              </div>
              <div style={{ fontSize:13, color:"#991B1B" }}>
                {auditFailed && (lang==="gr"?"Audit FAIL → bonus ακυρώνεται":"Audit FAIL → bonus nullified")}
                {fcGateFailed && (lang==="gr"?"Food Cost >38% → bonus ακυρώνεται":"Food Cost >38% → bonus nullified")}
                {ratingFailed && (lang==="gr"?"Rating <7.8 → bonus ακυρώνεται":"Rating <7.8 → bonus nullified")}
                {(quizPass===false||otjPass===false) && (lang==="gr"?"Training assessment fail → δεν πληροί τις προϋποθέσεις":"Training assessment fail → not eligible")}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.7)", textTransform:"uppercase", letterSpacing:2, marginBottom:4 }}>
                {lang==="gr"?"ΣΥΝΟΛΙΚΗ ΒΑΘΜΟΛΟΓΙΑ":"PERFORMANCE SCORE"}
              </div>
              <div style={{ fontFamily:"Bebas Neue,sans-serif", fontSize:72, color:"var(--lime)", lineHeight:1, letterSpacing:2 }}>{score}%</div>
              <div style={{ fontFamily:"Bebas Neue,sans-serif", fontSize:48, color:"#fff", letterSpacing:1 }}>€{bonus}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", marginTop:6 }}>
                {lang==="gr"
                  ? `Ζώνη: ${score}% → €${bonus} bonus`
                  : `Band: ${score}% → €${bonus} bonus`}
              </div>
            </>
          )}
        </div>
      )}

      {/* Bonus bands reference */}
      <div style={{ marginTop:16, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:16, overflow:"hidden" }}>
        <div style={{ background:"var(--green)", padding:"10px 16px", fontWeight:800, fontSize:13, color:"#fff" }}>
          {lang==="gr"?"Πίνακας Bonus":"Bonus Table"}
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"var(--surface2)" }}>
              <th style={{ padding:"8px 16px", textAlign:"left", fontSize:11, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:1 }}>{lang==="gr"?"Βαθμολογία":"Score"}</th>
              <th style={{ padding:"8px 16px", textAlign:"right", fontSize:11, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:1 }}>{lang==="gr"?"Bonus":"Bonus"}</th>
            </tr>
          </thead>
          <tbody>
            {BONUS_BANDS.map(b => {
              const isActive = score !== null && score >= b.min && score <= b.max;
              return (
                <tr key={b.min} style={{ borderTop:"1px solid var(--border)", background: isActive?"var(--green-light)":"transparent" }}>
                  <td style={{ padding:"10px 16px", fontSize:13, fontWeight: isActive?800:600, color: isActive?"var(--green)":"var(--text-primary)" }}>
                    {isActive && "→ "}{b.max === 999 ? "≥110%" : `${b.min}–${b.max}%`}
                  </td>
                  <td style={{ padding:"10px 16px", textAlign:"right", fontFamily:"Bebas Neue,sans-serif", fontSize:18, color: b.bonus===0?"var(--text-muted)":"var(--green)", letterSpacing:1 }}>
                    €{b.bonus}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

